
import { computed, defineComponent } from "vue";
import { useChannelSelector } from "@/composables/channelSelector";
import { ConversationChannel } from "@/types/channels";
import { showQrCode } from "@/dialogs/QrCodeDialog";
import MobileDetect from "mobile-detect";
import { useWidgetConfig } from "@/composables/widgetConfig";
import { useI18n } from "vue-i18n";

const baseButtonClasses =
  "py-2 px-3 text-sm font-semibold rounded-md shadow-lg focus:outline-none cursor-pointer transition-all";
const buttonClasses = `${baseButtonClasses} bg-superagent text-white hover:shadow-superagent/30`;
const secondaryButtonClasses = `${baseButtonClasses} bg-transparent border-2 border-superagent text-gray-700 hover:shadow-gray-700/30`;
const whatsappButtonClasses = `${baseButtonClasses} bg-transparent border-2 border-whatsapp text-gray-700 hover:shadow-gray-700/30`;

export default defineComponent({
  name: "MessengerChannelPicker",
  components: {},
  setup(props) {
    const { setChannel } = useChannelSelector();
    const { configuration } = useWidgetConfig();
    const { t } = useI18n();

    const offerWhatsapp = computed(() =>
      configuration.value?.channels?.some(
        (i) =>
          i.channel === "whatsapp" && i.offerStartDirect && i.startDirectTarget
      )
    );

    const offerEmail = computed(() =>
      configuration.value?.channels?.some(
        (i) =>
          i.channel === "email" && i.offerStartDirect && i.startDirectTarget
      )
    );

    const offerSms = computed(() =>
      configuration.value?.channels?.some(
        (i) => i.channel === "sms" && i.offerStartDirect && i.startDirectTarget
      )
    );

    if (!offerWhatsapp.value && !offerSms.value && !offerEmail.value) {
      setChannel(ConversationChannel.Website);
    }

    const mobileDetect = new MobileDetect(window.navigator.userAgent);

    const getTarget = (channel: string) => {
      const target = configuration.value?.channels?.filter(
        (i) =>
          i.channel === channel && i.offerStartDirect && i.startDirectTarget
      )[0]?.startDirectTarget;

      if (!target) {
        throw new Error("Couldn't find URL for WhatsApp");
      }

      return target;
    };

    const selectChannel = (channel: ConversationChannel) => {
      const isMobile = !!mobileDetect.mobile();

      switch (channel) {
        case ConversationChannel.WhatsApp: {
          const target = getTarget("whatsapp");

          const url = `https://wa.me/${target}`;

          if (isMobile) {
            window.open(url);
          } else {
            showQrCode(t("messengerChannelPicker.whatsAppQr"), url);
          }
          break;
        }
        case ConversationChannel.Sms: {
          const target = getTarget("sms");

          const url = `sms:${target}`;
          if (isMobile) {
            window.open(url);
          } else {
            showQrCode(t("messengerChannelPicker.smsQr"), url);
          }
          break;
        }
        case ConversationChannel.Email: {
          const target = getTarget("email");

          const url = `mailto:${target}`;
          window.open(url);
          break;
        }
        case ConversationChannel.Website: {
          setChannel(channel);
          break;
        }
      }
    };

    return {
      offerWhatsapp,
      offerEmail,
      offerSms,
      buttonClasses,
      secondaryButtonClasses,
      whatsappButtonClasses,
      setChannel,
      selectChannel,
      ConversationChannel,
      t,
    };
  },
});
